// Recordings that fit more than one mirrored call, in Blueprint (BL-083, BL-189): an owner or manager says
// which call each one is. A Section of RowList rows: the number it came from, when and how long, and a
// Link for each call it fits (the first two at rest, the rest under More). A page at a time, Show more
// reading on (list caps, BL-110); nothing at all when none waits. Riverstone's is CallTranscript.tsx's.
import { useState } from 'react';
import { RpcError } from '@flowology/client';
import { RowList, StateMark } from '@flowology/ui-web/fl';
import { Section } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { usePaged } from '../blueprint/paged.ts';
import { phoneText } from '../types.ts';
import { lengthText, MATCH_INPUT, matchRows } from './transcript-model.ts';

const say = (e: unknown) => (e instanceof RpcError ? e.message : 'Couldn’t load it. Try again.');

export function MatchRecordings({
  whenOf,
  toast,
}: {
  whenOf: (iso: string) => string;
  toast: (words: string) => void;
}) {
  const list = usePaged('calls.recordingsToMatch', MATCH_INPUT, matchRows);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pick = async (recordingId: string, callId: string) => {
    setBusy(recordingId);
    setError(null);
    try {
      await client.call('calls.linkRecording', { recordingId, callId });
      toast('Recording linked to the call');
      list.reload();
    } catch (e) {
      setError(say(e));
    } finally {
      setBusy(null);
    }
  };
  const fits = (list.rows ?? []).filter((r) => r.candidates.length > 1);
  if (!fits.length) return null;
  const problem = error ?? list.error;
  return (
    <Section title="Which call is this recording?" count={list.first?.total ?? fits.length}>
      <RowList
        label="Recordings to match"
        rows={fits.map((r) => ({
          id: r.recordingId,
          name: phoneText(r.from),
          lines: [{ text: `${whenOf(r.startedAt)}, ${lengthText(r.durationSeconds)}` }],
          atRest: 2,
          actions: r.candidates.map((c) => ({
            id: c.callId,
            text: `${whenOf(c.startedAt)}, ${c.customerName ?? 'Unknown caller'}`,
            label: `It’s the call at ${whenOf(c.startedAt)} from ${c.customerName ?? 'an unknown caller'}`,
            isDisabled: busy !== null,
            onPress: () => void pick(r.recordingId, c.callId),
          })),
        }))}
        note="More than one call fits each of these. Pick the one it is."
        {...(list.more && !list.busy ? { onMore: () => list.more?.() } : {})}
      />
      {problem && (
        <p className="calls-bp-bad" role="alert">
          <StateMark state="problem" />
          {problem}
        </p>
      )}
    </Section>
  );
}
